import { type CSSProperties, createContext, type ReactNode, useContext, useEffect } from "react";
import { cx } from "../tv";
import { loadBrandFace } from "./fonts.ts";

/** The agency's public mark: its name, logo, brand colour, font and favicon. Public pages and the client portal show the logo
 *  and use the colour as their accent in place of the platform's. The server only accepts a colour that passes 4.5:1 against white. */
export type BrandMark = {
  name: string;
  logo: string | null;
  colour: string | null;
  font?: string | null;
  favicon?: string | null;
};

const BrandContext = createContext<BrandMark | null>(null);

/** The nine font families a brand can pick. The five system ids map to stacks the browser has; the four named faces (X21-RESA-06)
 *  are self-hosted, and their @font-face rules load with the brand only when chosen (fonts.ts). */
export const BRAND_FONT_STACK: Record<string, string> = {
  system: "system-ui, -apple-system, Segoe UI, sans-serif",
  serif: "Georgia, Times New Roman, serif",
  humanist: "Gill Sans, Trebuchet MS, sans-serif",
  rounded: "ui-rounded, Arial Rounded MT Bold, system-ui, sans-serif",
  mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
  inter: "Inter, system-ui, sans-serif",
  "dm-sans": "DM Sans, system-ui, sans-serif",
  "space-grotesk": "Space Grotesk, system-ui, sans-serif",
  "jetbrains-mono": "JetBrains Mono, ui-monospace, monospace",
};

/** The brand font as the font family of one subtree. Undefined for no mark, the system font, an unknown id or no font. */
export function brandFontStyle(mark: BrandMark | null | undefined): CSSProperties | undefined {
  const font = mark?.font;
  if (!font || font === "system" || !Object.prototype.hasOwnProperty.call(BRAND_FONT_STACK, font)) return undefined;
  return { fontFamily: BRAND_FONT_STACK[font] };
}

/** Gives every page below it the agency's mark. Without one, the page keeps the platform accent and shows no logo. While it is
 *  mounted with a mark that has a favicon, the page's icon is the agency's; the previous icon comes back on unmount. */
export function BrandProvider({ mark, children }: { mark: BrandMark | null | undefined; children: ReactNode }) {
  const favicon = mark?.favicon && mark.favicon.startsWith("https://") ? mark.favicon : null;
  const face = mark?.font;
  // the named face's @font-face rules load only once the brand picks it (fonts.ts)
  useEffect(() => {
    void loadBrandFace(face);
  }, [face]);
  useEffect(() => {
    if (typeof document === "undefined" || !favicon) return;
    const created = !document.head.querySelector('link[rel~="icon"]');
    const link = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]') ?? document.createElement("link");
    if (created) {
      link.rel = "icon";
      document.head.appendChild(link);
    }
    const before = link.getAttribute("href");
    link.setAttribute("href", favicon);
    return () => {
      if (before) link.setAttribute("href", before);
      else link.remove();
    };
  }, [favicon]);
  const provided = <BrandContext.Provider value={mark ?? null}>{children}</BrandContext.Provider>;
  const style = brandFontStyle(mark);
  return style ? <div style={style} className="contents">{provided}</div> : provided;
}

/** The mark of the nearest BrandProvider, or null. */
export function useBrandMark(): BrandMark | null {
  return useContext(BrandContext);
}

/** The brand colour as the accent of one subtree. Text on the accent is white, which the server's contrast check allows. */
export function brandAccentStyle(mark: BrandMark | null): CSSProperties | undefined {
  return mark?.colour ? ({ "--accent": mark.colour, "--accent-fg": "white" } as CSSProperties) : undefined;
}

/** The agency logo. Its alt text is empty because the agency's name sits beside it; the caller sets its size. */
export function BrandLogo({ src, className }: { src: string; className?: string }) {
  return <img src={src} alt="" className={cx("shrink-0 object-contain", className)} />;
}
